import type { AccountProgramCapacity } from "@/lib/brokerAccounts";
import type { BondSubmission } from "@/lib/brokerBonds";
import type { CapacityProject } from "@/lib/capacityRequest";
import {
  bondKindLabel,
  bondStatusLabel,
  readBondLifecycle,
  readBondRows,
  readCapacityCheck,
  type BondCapacityCheck,
  type BondKind,
  type BondPhaseKey,
  type BondRowView,
  type BondStatus,
} from "@/lib/bondStateModel";
import { formatPreapprovalMoney } from "@/lib/bondValueDisplay";
import { bidHoldNote } from "@/lib/bondLifecycleTracker";
import type { HeaderPill, PillTone } from "@/lib/submissionShell";

// BRK-S415 — the Submission Profile tab's reads, beneath the Bond Lifecycle:
// the CURRENT-BOND card and the PROGRAM LIMITS block.
//
// NOTHING HERE DECIDES A VERDICT (CLAUDE.md #7). The bonds are PMS's rows
// (`bonds[]`, PMS-S836), the phase is PMS's (`lifecycle`, PMS-S837) and
// over/within is PMS's (`capacityCheck`, PMS-S841) — all read ahead of the
// pin through `bondStateModel.ts`. What BRK adds is presentation: WHICH rows
// the card names (the bonds of the phase the job is in), and the meters' sums
// of published figures. The pill never comes from comparing those sums with a
// limit: a client comparison is a second answer that could disagree with the
// server's, so without `capacityCheck` the block shows the meters and no
// verdict at all.
//
// THE OVERAGE (BRK-S502, the October wireframe; D18 since BRK-S511): WHETHER
// the bond is over, which limit, BY HOW MUCH and against what figure are all
// the served `capacityCheck` (`overBy`, `limitAmount`) — the red sentence
// formats them ("$400,000 over the $1,000,000 single bond limit") and works
// out nothing. Never a reason, score or tier — PMS publishes none.

// ── The current-bond card ────────────────────────────────────────────────────

/** The bonds that headline each phase, in order of preference: the bid
 *  bond, then the final bonds (a supply bond rides alongside and is listed on
 *  the Bonds tab), then the maintenance bond. The AWARD phase (D11) holds the
 *  issued bid bond while the result is awaited, so it falls back to the bid
 *  bond until a final bond is requested. */
const PHASE_KINDS: Record<Exclude<BondPhaseKey, "closed">, BondKind[][]> = {
  bid: [["bid"]],
  award: [["performance", "payment"], ["bid"]],
  performance_period: [["performance", "payment"]],
  maintenance: [["maintenance"]],
};

/** Once closed, the LAST bonds the job carried: maintenance, else the final
 *  bonds, else a supply bond, else the bid bond. */
const CLOSED_KIND_GROUPS: BondKind[][] = [
  ["maintenance"],
  ["performance", "payment"],
  ["supply"],
  ["bid"],
];

/** A bond on its way or in force. */
const LIVE: readonly BondStatus[] = [
  "requested",
  "in_underwriting",
  "approved",
  "issued",
  "converting",
];

/** A bond that was in force at some point. */
const WAS_IN_FORCE: readonly BondStatus[] = [
  "issued",
  "converting",
  "released",
  "exonerated",
];

const is = (statuses: readonly BondStatus[]) => (r: BondRowView) =>
  r.status !== null && statuses.includes(r.status);
const ofKinds = (kinds: readonly BondKind[]) => (r: BondRowView) =>
  r.kind !== null && kinds.includes(r.kind);

/**
 * The rows the card names: the bonds of the phase the job is in (an edit's
 * voided bid bond gives way to its reissue; a bond not yet requested is never
 * "current"). Once closed, the last bonds that were in force. Without a
 * lifecycle, the bonds on their way or in force. Empty when nothing fits.
 */
export function currentBondRows(order: {
  bonds?: unknown;
  lifecycle?: unknown;
}): BondRowView[] {
  const rows = readBondRows(order.bonds);
  const phase = readBondLifecycle(order.lifecycle)?.phase ?? null;

  if (phase === "closed") {
    for (const kinds of CLOSED_KIND_GROUPS) {
      const group = rows.filter(
        (r) => ofKinds(kinds)(r) && is(WAS_IN_FORCE)(r),
      );
      if (group.length > 0) return group;
    }
    return [];
  }
  if (phase !== null) {
    for (const kinds of PHASE_KINDS[phase]) {
      const ofPhase = rows.filter(
        (r) => ofKinds(kinds)(r) && r.status !== "not_requested",
      );
      const standing = ofPhase.filter(
        (r) => r.status !== "voided" && r.status !== "cancelled",
      );
      if (standing.length > 0) return standing;
      if (ofPhase.length > 0) return ofPhase;
    }
  }
  return rows.filter(is(LIVE));
}

const SHORT_KIND: Record<BondKind, string> = {
  bid: "Bid",
  performance: "Performance",
  payment: "Payment",
  supply: "Supply",
  maintenance: "Maintenance",
};

/** "Bid bond"; several read as the wireframe joins them — "Performance &
 *  Payment bonds", "Performance, Payment & Supply bonds". */
export function bondsTitle(kinds: readonly (BondKind | null)[]): string {
  const unique = [...new Set(kinds)];
  if (unique.length === 1) return bondKindLabel(unique[0]);
  const names = unique.map((k) => (k === null ? "Other" : SHORT_KIND[k]));
  return `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]} bonds`;
}

/** Each bond status's pill tone; the words are `bondStatusLabel`'s. */
const STATUS_TONES: Record<BondStatus, PillTone> = {
  not_requested: "neutral",
  requested: "active",
  in_underwriting: "attention",
  approved: "attention",
  issued: "done",
  voided: "attention",
  converting: "active",
  released: "neutral",
  exonerated: "neutral",
  cancelled: "neutral",
};

/** A bond status as the card's pill. CLOSED: a status this build has not
 *  adopted reads "Status unavailable", neutral — never a blank. */
export function currentBondPill(status: BondStatus | null): HeaderPill {
  return {
    label: bondStatusLabel(status),
    tone: status === null ? "neutral" : STATUS_TONES[status],
  };
}

/** The card's pill (the wireframe's vocabulary): PMS's own hold while the
 *  bid phase is held ("On hold · GIA Form needed", red), "Issued" for an
 *  issued bid bond, otherwise the headline bond's status. */
function cardPill(
  rows: readonly BondRowView[],
  rawLifecycle: unknown,
): HeaderPill {
  const first = rows[0];
  const hold = bidHoldNote(rawLifecycle);
  if (hold !== null && first.status !== "issued") {
    return { label: hold, tone: "danger" };
  }
  if (first.kind === "bid" && first.status === "issued") {
    return { label: "Issued", tone: "done" };
  }
  return currentBondPill(first.status);
}

export type CurrentBondEyebrow =
  | "Current bond"
  | "Current bonds"
  | "Final status";

export interface CurrentBondView {
  eyebrow: CurrentBondEyebrow;
  title: string;
  /** The form titles, " · "-joined; null when PMS named none. */
  form: string | null;
  pill: HeaderPill;
  /** PMS's own note strip (`currentBondNote`, PMS-S837) — served
   *  display copy, never computed; null when PMS sent none. */
  note: string | null;
}

/** The current-bond card (BRK-S415, BRK-S502), or null with no row to name
 *  and the job still open. Once closed with nothing that was ever in force,
 *  it is the wireframe's "FINAL STATUS · No active bonds". */
export function currentBond(order: {
  bonds?: unknown;
  lifecycle?: unknown;
  currentBondNote?: string | null;
}): CurrentBondView | null {
  const rows = currentBondRows(order);
  const lifecycle = readBondLifecycle(order.lifecycle);
  const closed = lifecycle?.phase === "closed";
  const note =
    typeof order.currentBondNote === "string" &&
    order.currentBondNote.trim() !== ""
      ? order.currentBondNote
      : null;
  if (rows.length === 0) {
    if (!closed) return null;
    return {
      eyebrow: "Final status",
      title: "No active bonds",
      form: null,
      pill: { label: "Closed", tone: "neutral" },
      note,
    };
  }
  const forms = [
    ...new Set(rows.flatMap((r) => (r.formTitle ? [r.formTitle] : []))),
  ];
  const form = forms.length > 0 ? forms.join(" · ") : null;
  return {
    eyebrow: closed
      ? "Final status"
      : rows.length > 1
        ? "Current bonds"
        : "Current bond",
    title: bondsTitle(rows.map((r) => r.kind)),
    // Closed: the last bond's form, as the wireframe words it.
    form: form !== null && closed ? `Last bond: ${form}` : form,
    pill: cardPill(rows, order.lifecycle),
    note,
  };
}

/** The contract price the current bonds were written on — what capacity is
 *  measured against. */
export function currentContractPrice(
  rows: readonly BondRowView[],
): number | null {
  return rows.find((r) => r.contractPrice !== null)?.contractPrice ?? null;
}

// ── The Program limits block ─────────────────────────────────────────────────

/** No program (D2): readiness is advisory, never a gate, so the wireframe's
 *  "It can't move beyond Bid Bond Request until the client enrolls" reads as
 *  what is needed, not as a block. */
export const NO_PROGRAM_SENTENCE =
  "No surety program, so there are no limits to check this bond against. Program enrollment is needed before issuance.";

/** WHICH limit a bond is over, with no figure — the Program Enrollment
 *  button's Advance-redirect confirm (BRK-S422), which names no amount. */
export const OVER_SENTENCE: Record<"single" | "aggregate", string> = {
  single: "This bond is over the single bond limit.",
  aggregate: "It would take the aggregate over the program limit.",
};

/** Decision 8: "Signal will review", never "Underwriting will review". */
const REVIEW = "Signal will review before issuing";

const PILLS = {
  none: { label: "No program", tone: "danger" },
  needsPrice: { label: "Contract price needed", tone: "neutral" },
  released: { label: "Released · not counted", tone: "neutral" },
  over: { label: "Over program limits", tone: "danger" },
  within: { label: "Within program limits", tone: "done" },
} as const satisfies Record<string, HeaderPill>;

const money = formatPreapprovalMoney;

/** Phases whose bonds no longer count against the program: completion
 *  releases the job's share (proposal §10.3). */
export const RELEASED_PHASES: readonly BondPhaseKey[] = [
  "maintenance",
  "closed",
];

export interface ProgramLimitsView {
  /** The verdict pill; null when PMS published no `capacityCheck`. */
  pill: HeaderPill | null;
  /** No program on the account: say so, and draw no meters. */
  noProgram: boolean;
  /** This bond against the single bond limit. `percent` is unclamped. */
  single: { bond: number; limit: number; percent: number } | null;
  /** Other bonds + this bond against the aggregate. `thisBond` is null when
   *  the bond is released and not counted; `availableAfter` is negative when
   *  the bond takes the aggregate over ("$350,000 over the aggregate"). */
  aggregate: {
    other: number;
    thisBond: number | null;
    limit: number;
    availableAfter: number;
    /** The bond is not issued yet: "This bond $500,000 (once issued)". */
    onceIssued: boolean;
  } | null;
  /** The meter PMS's verdict says is over — drawn red. */
  overLimit: "single" | "aggregate" | null;
  /** The red sentence under the meters when PMS says over; null within. */
  warning: string | null;
}

/**
 * The Program limits block, or null when the order view carries no `program`
 * key at all (a PMS before PMS-S720 — saying "No program" would be a claim
 * nobody made). `capacity` is the ACCOUNT's utilization and `projects` its
 * capacity-projects read; either unread (null) drops the aggregate meter
 * rather than guessing what the other bonds hold.
 */
export function programLimits({
  order,
  capacity,
  projects,
  advanceOffered = false,
}: {
  order: Pick<BondSubmission, "id" | "program" | "capacityCheck"> & {
    bonds?: unknown;
    lifecycle?: unknown;
  };
  capacity: AccountProgramCapacity | null;
  projects: readonly CapacityProject[] | null;
  /** The account is on Signal Express: the sentence offers Advance. */
  advanceOffered?: boolean;
}): ProgramLimitsView | null {
  if (order.program === undefined) return null;
  const program = order.program;
  if (program === null) {
    return {
      pill: PILLS.none,
      noProgram: true,
      single: null,
      aggregate: null,
      overLimit: null,
      warning: null,
    };
  }
  const contract = currentContractPrice(currentBondRows(order));
  if (contract === null) {
    return {
      pill: PILLS.needsPrice,
      noProgram: false,
      single: null,
      aggregate: null,
      overLimit: null,
      warning: null,
    };
  }
  const phase = readBondLifecycle(order.lifecycle)?.phase ?? null;
  const released = phase !== null && RELEASED_PHASES.includes(phase);
  const check = readCapacityCheck(order.capacityCheck);

  const rows = currentBondRows(order);
  const onceIssued = !rows.some((r) => r.status === "issued");
  const single =
    program.singleLimit === null || program.singleLimit <= 0
      ? null
      : {
          bond: contract,
          limit: program.singleLimit,
          percent: Math.round((contract / program.singleLimit) * 100),
        };

  let aggregate: ProgramLimitsView["aggregate"] = null;
  if (
    program.aggregateLimit !== null &&
    program.aggregateLimit > 0 &&
    capacity !== null &&
    projects !== null
  ) {
    // The account's figure counts THIS order's open allocation too; it is
    // taken out so "other bonds" is everything but this one.
    const own = projects.find((p) => p.orderId === order.id)?.amount ?? 0;
    const other = Math.max(0, capacity.aggregateUtilized - own);
    const thisBond = released ? null : contract;
    aggregate = {
      other,
      thisBond,
      limit: program.aggregateLimit,
      availableAfter: program.aggregateLimit - other - (thisBond ?? 0),
      onceIssued,
    };
  }

  let pill: HeaderPill | null = null;
  let warning: string | null = null;
  let overLimit: ProgramLimitsView["overLimit"] = null;
  if (released) {
    pill = PILLS.released;
  } else if (check !== null) {
    pill = check.withinLimits ? PILLS.within : PILLS.over;
    if (!check.withinLimits) {
      overLimit = check.overLimit;
      warning = overageSentence({
        check,
        maxSingleLimit: program.maxSingleLimit,
        advanceOffered,
      });
    }
  }

  return { pill, noProgram: false, single, aggregate, overLimit, warning };
}

/**
 * The red sentence under the meters (the October wireframe): which limit PMS
 * says is over and by how much — `capacityCheck.overBy` against
 * `capacityCheck.limitAmount`, SERVED (PMS-S841, D18; the client arithmetic
 * was deleted in BRK-S511) — then what happens: Signal reviews, and on Signal
 * Express the client can apply for Advance; above the program maximum no
 * upgrade helps. A verdict without its figures names the limit and no
 * amount, never a figure the SPA worked out.
 */
function overageSentence({
  check,
  maxSingleLimit,
  advanceOffered,
}: {
  check: BondCapacityCheck;
  maxSingleLimit: number | null;
  advanceOffered: boolean;
}): string {
  const { overLimit, overBy, limitAmount, hardOver } = check;
  const parts: string[] = [];
  if (overLimit === "single" && overBy !== null && limitAmount !== null) {
    parts.push(
      `This bond is ${money(overBy)} over the ${money(limitAmount)} single bond limit.`,
    );
  } else if (
    overLimit === "aggregate" &&
    overBy !== null &&
    limitAmount !== null
  ) {
    parts.push(
      `It would take the aggregate ${money(overBy)} over the ${money(limitAmount)} limit.`,
    );
  } else if (overLimit !== null) {
    parts.push(OVER_SENTENCE[overLimit]);
  } else {
    parts.push("This bond is over the program limits.");
  }
  if (hardOver) {
    // The maximum is null until Signal saves its program terms (D22): the
    // sentence then names no figure rather than $0.
    const maximum =
      maxSingleLimit === null ? "the" : `the ${money(maxSingleLimit)}`;
    parts.push(
      `That is above ${maximum} maximum single bond limit on any Signal program, so upgrading won’t cover it. ${REVIEW}.`,
    );
  } else if (advanceOffered) {
    parts.push(
      `${REVIEW}, or the client can apply for Signal Advance for higher limits.`,
    );
  } else {
    parts.push(`${REVIEW}.`);
  }
  return parts.join(" ");
}
